import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CalendarClock, MapPin, AlertTriangle } from "lucide-react";

// V1.93 · Vista rápida de SOLO el horario disponible del alumno y sus
// incidencias temporales, para organizar clases sin abrir toda la ficha.
const DAYS = [
  ["monday", "Lunes"], ["tuesday", "Martes"], ["wednesday", "Miércoles"],
  ["thursday", "Jueves"], ["friday", "Viernes"], ["saturday", "Sábado"], ["sunday", "Domingo"],
];
const dmy = (iso) => String(iso || "").split("-").reverse().join("-");

export default function StudentAvailabilityDialog({ student, onClose }) {
  const sid = student?.id || "";
  const [availability, setAvailability] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!sid) return;
    setLoading(true);
    try {
      const [av, inc, locs] = await Promise.all([
        api.get(`/students/${sid}/availability`),
        api.get(`/students/${sid}/incidents`),
        api.get("/locations"),
      ]);
      const pickupId = av.data?.habitual_pickup_location_id || "";
      const pickup = (locs.data || []).find((l) => l.id === pickupId);
      setAvailability({ ...(av.data || {}), habitual_pickup_name: pickup?.name || "" });
      setIncidents(inc.data || []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [sid]);

  useEffect(() => { load(); }, [load]);

  const weekly = availability?.weekly_slots || {};

  return (
    <Dialog open={!!student} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid="student-availability-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <CalendarClock className="h-4 w-4 text-orange-600" />
            Horario disponible · {student?.name || ""}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-6 text-sm text-neutral-500">Cargando horario…</div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl border border-neutral-200 divide-y divide-neutral-100">
              {DAYS.map(([key, label]) => {
                const slots = (weekly[key] || []).filter((s) => s.start && s.end);
                return (
                  <div key={key} className="flex items-center gap-3 px-3 py-1.5" data-testid={`avail-day-${key}`}>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 w-24 shrink-0">
                      {label}
                    </span>
                    {slots.length === 0 ? (
                      <span className="text-xs text-neutral-400 italic">No disponible</span>
                    ) : (
                      <span className="flex flex-wrap gap-1.5">
                        {slots.map((s, i) => (
                          <span key={i} className="rounded-md bg-emerald-100 text-emerald-800 px-2 py-0.5 text-xs font-mono font-semibold">
                            {s.start}–{s.end}
                          </span>
                        ))}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-3 text-xs text-neutral-600">
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5 text-neutral-400" />
                Recogida habitual: <b className="text-neutral-800">{availability?.habitual_pickup_name || availability?.neighborhood || "sin definir"}</b>
              </span>
              {availability?.preferences && (
                <span className="italic text-neutral-500">“{availability.preferences}”</span>
              )}
            </div>

            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-600">
                  Incidencias temporales ({incidents.length})
                </span>
              </div>
              {incidents.length === 0 ? (
                <div className="rounded-lg border border-dashed border-neutral-200 py-3 text-center text-xs text-neutral-500"
                  data-testid="avail-no-incidents">
                  Sin incidencias registradas.
                </div>
              ) : (
                <div className="rounded-lg border border-amber-200 bg-amber-50 divide-y divide-amber-100" data-testid="avail-incidents">
                  {incidents.map((i) => (
                    <div key={i.id} className="px-3 py-1.5 text-xs flex items-center gap-2">
                      <span className="font-mono tabular-nums text-amber-900">
                        {dmy(i.date_start)}{i.date_end && i.date_end !== i.date_start ? ` → ${dmy(i.date_end)}` : ""}
                      </span>
                      <span className="font-semibold text-amber-900">{i.reason || "Incidencia"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
